import { useState, memo, useMemo, FC } from 'react';

import If from 'global/ErrorHandlers/If';
import { trackAutoBuyOrderCanceled } from 'lib/analytics/autoOrder';
import { ORDER_TYPE } from 'constants/trading';
import tradingModalStore from 'global/TradingModal/store';
import useCashInfoStore from 'global/Securities/stores/useCashInfoStore';
import { TransactionFormType } from 'typings/transactionForm';
import useUIConfigStore from 'stores/useUIConfigStore';
import {
  PaymentMethod,
  TransactionType,
} from 'global/Securities/types/transaction';
import { PortfolioItem } from 'global/Securities/types/api/portfolio';
import { useFliptBoolean } from 'lib/flipt/hooks';
import { FeatureFlagKeys } from 'lib/flipt/constants/flagKeys';

import tradingDataStore from 'lib/stores/tradingData';
import { EmittenInfoResponse } from 'global/Securities/types/api/emittenInfo';
import { StockType } from 'global/Securities/types/stockType';
import useTradingStore from 'features/transaction/useTradingStore';
import useTermsAndConditionStore from 'global/Securities/stores/useTermsAndConditionStore';
import { TnCFeatureIDEnum } from 'global/Securities/types/termsAndCondition';
import { expiryOption } from 'global/TradingModal/constant';
import { Flex } from '@deprecated-ui/common';
import TableOrderbook from '../../TableOrderbook';
import FormHeader from '../../components/Header';
import FormBuy from './FormBuy';
import FormSell from './FormSell';
import ExerciseForm from './FormExercise';
import {
  ModalFormWrapper,
  TransactionModalContainer,
  FormWrapper,
  FreezeLayer,
} from '../../styled';
import realTradingStore from '../store';
import { useBondsModalStore } from '../ModalBonds/stores/useBondsModalStore';
import useBondsAnalytics from '../ModalBonds/useBondsAnalytics';
import { MODAL_TYPE } from '../types';

interface BuySellModalProps {
  visible: boolean;
  onUpdateSymbol: (symbol) => void;
  closeModal: () => void;
  onSwitchTab: (prop: string) => void;
  portfolioData: PortfolioItem;
  onSubmitExercise: () => void;
}

const transactionToOrderType = {
  [TransactionType.Buy]: ORDER_TYPE.BUY,
  [TransactionType.Sell]: ORDER_TYPE.SELL,
};

const NativeTransactionModal: FC<BuySellModalProps> = ({
  visible,
  onUpdateSymbol,
  closeModal,
  onSwitchTab,
  portfolioData,
  onSubmitExercise,
}) => {
  const isFastOrderEnabled = useFliptBoolean(FeatureFlagKeys.FAST_ORDER);
  const setModal = useTradingStore((s) => s.setModal);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const resetCashInfo = useCashInfoStore((s) => s.resetCashInfo);
  const { FRBondActionTrackers } = useBondsAnalytics();

  const { showOrderbook, showTabs } = tradingModalStore();
  const orderType = useTradingStore((s) => s.orderType);
  const resetOrderData = realTradingStore((s) => s.resetOrderData);
  const setUIValue = useUIConfigStore((s) => s.setUIValue);
  const openBondsBuyModal = useBondsModalStore((s) => s.openBuyModal);
  const company = tradingDataStore(
    (s) => s.company.data as EmittenInfoResponse,
  );

  const loadTnC = useTermsAndConditionStore((s) => s.loadTnC);

  const symbol = useTradingStore((s) => s.symbol);

  const {
    price,
    expiry,
    shares,
    type,
    triggerPrice,
    isSplitOrderActive,
    splitQuantity,
    splitRangeMin,
    splitRangeMax,
    dayTradeActive,
    dayTradeMultiplier,
    configType,
    availableLot,
  } = realTradingStore();

  const onSubmit = () => {
    const gtc = expiryOption.findIndex((e) => e === expiry);

    const previewData = {
      price,
      shares,
      triggerPrice,
      gtc: gtc < 0 ? 0 : gtc,
      symbol,
      isSplitOrderActive,
      splitQuantity,
      splitRangeMin,
      splitRangeMax,
      multiplier: String(dayTradeMultiplier),
      type,
      isDayTrade: dayTradeActive,
      orderType,
      configType,
      availableLot,
      companyType: company?.type_company ?? '',
    };

    setModal(MODAL_TYPE.PREVIEW, { previewData });
  };

  const available_lot = portfolioData?.available_lot || 0;

  const isBuy =
    orderType === ORDER_TYPE.BUY || orderType === ORDER_TYPE.AMEND_BUY;
  const isSell =
    orderType === ORDER_TYPE.SELL || orderType === ORDER_TYPE.AMEND_SELL;
  const isExercise = orderType === ORDER_TYPE.EXERCISE;
  const isAmend = useMemo(
    () => [ORDER_TYPE.AMEND_BUY, ORDER_TYPE.AMEND_SELL].includes(orderType),
    [orderType],
  );

  const isFormTypeSelectVisible = useMemo(
    () =>
      isFastOrderEnabled &&
      [ORDER_TYPE.BUY, ORDER_TYPE.SELL].includes(orderType) &&
      [
        StockType.Saham,
        StockType.Waran,
        StockType.Right,
        StockType.Etf,
      ].includes(company?.type_company.toLowerCase() as StockType),
    [isFastOrderEnabled, isAmend],
  );

  const defaultTabValue = useMemo(() => {
    if (
      available_lot <= 0 ||
      [(ORDER_TYPE.BUY, ORDER_TYPE.AMEND_BUY)].includes(orderType)
    ) {
      return TransactionType.Buy;
    }
    if ([ORDER_TYPE.SELL, ORDER_TYPE.AMEND_SELL].includes(orderType)) {
      return TransactionType.Sell;
    }

    return TransactionType.Buy;
  }, [orderType]);

  const handleUpdateSymbol = (selected: string, isBonds?: boolean) => {
    if (isBonds) {
      openBondsBuyModal(selected);
      FRBondActionTrackers.onOrderButtonClicked(
        'Buy',
        selected,
        'Search Company Page',
      );
      closeModal();
      return;
    }
    onUpdateSymbol(selected);
    setIsSearchActive(false);
  };

  const handleClose = () => {
    resetCashInfo();
    closeModal();
    resetOrderData();
    trackAutoBuyOrderCanceled();

    setUIValue('transactionPaymentMethod', PaymentMethod.TradingBalance);
  };

  const handleFormTypeChange = (value: TransactionFormType) => {
    if (value === TransactionFormType.FastOrder) handleClose();
  };

  const handleSwitchTab = (value: TransactionType) => {
    onSwitchTab(transactionToOrderType[value]);
  };

  const handleShowSplitOrderInfo = () => {
    setModal(MODAL_TYPE.SPLIT_ORDER_INFO);
  };

  const handleShowSplitMethodInfo = () => {
    setModal(MODAL_TYPE.SPLIT_ORDER_METHOD_INFO);
  };

  const handleShowTradingLimitTnc = () => {
    loadTnC(
      TnCFeatureIDEnum.FEATURE_TRADING_LIMIT,
      /**
       * If user is not yet accepted the TnC,
       * then show the modal using the callback
       */
      () => {
        setUIValue('tradingLimitTnc', false);
        setUIValue('transactionPaymentMethod', PaymentMethod.TradingBalance);
        setModal(MODAL_TYPE.TRADING_LIMIT_TNC);
      },
      /**
       * If user was accepted the TnC before,
       * then show continue their action to use the Trading limit as payment
       */
      () => {
        setUIValue('tradingLimitTnc', true);
        setUIValue('transactionPaymentMethod', PaymentMethod.TradingLimit);
        setModal(MODAL_TYPE.TRANSACTION);
      },
    );
  };

  return (
    <TransactionModalContainer
      width="auto"
      data-cy="transaction-modal-real"
      visible={visible}
      onCancel={handleClose}
    >
      <Flex style={{ gap: 30 }}>
        <If condition={showOrderbook && !isExercise}>
          <TableOrderbook />
        </If>
        <ModalFormWrapper>
          {isSearchActive && (
            <FreezeLayer onClick={() => setIsSearchActive(false)} />
          )}
          <FormWrapper>
            <FormHeader
              orderType={orderType}
              defaultTabsValue={defaultTabValue}
              showTabs={showTabs}
              isSearchActive={isSearchActive}
              disableTabs={available_lot <= 0}
              setIsSearchActive={() => setIsSearchActive(!isSearchActive)}
              formTypeSelectVisible={isFormTypeSelectVisible}
              onUpdateSymbol={handleUpdateSymbol}
              onFormTypeChange={handleFormTypeChange}
              onTabsChange={handleSwitchTab}
            />

            {isBuy && (
              <FormBuy
                showOrderTypeModal={() => setModal(MODAL_TYPE.ORDER_TYPE)}
                showAutoBuyTncModal={() => setModal(MODAL_TYPE.AUTOBUY_TNC)}
                showSpecialMonitoringModal={() =>
                  setModal(MODAL_TYPE.SPECIAL_MONITORING)
                }
                onShowTradingLimitTnc={handleShowTradingLimitTnc}
                onShowSplitOrderInfo={handleShowSplitOrderInfo}
                onShowSplitOrderMethodInfo={handleShowSplitMethodInfo}
                onSubmit={onSubmit}
                onShowDayTradeOnboarding={() =>
                  setModal(MODAL_TYPE.DAY_TRADE_ONBOARDING)
                }
                onShowDayTradeUnavailable={() =>
                  setModal(MODAL_TYPE.DAY_TRADE_UNAVAILABLE)
                }
              />
            )}

            {isSell && (
              <FormSell
                showSpecialMonitoringModal={() =>
                  setModal(MODAL_TYPE.SPECIAL_MONITORING)
                }
                onShowSplitOrderInfo={handleShowSplitOrderInfo}
                onShowSplitOrderMethodInfo={handleShowSplitMethodInfo}
                onSubmit={onSubmit}
                onShowDayTradeUnavailable={() =>
                  setModal(MODAL_TYPE.DAY_TRADE_UNAVAILABLE)
                }
              />
            )}
            {isExercise && <ExerciseForm onSubmit={onSubmitExercise} />}
          </FormWrapper>
        </ModalFormWrapper>
      </Flex>
    </TransactionModalContainer>
  );
};

export default memo(NativeTransactionModal);
